import { motion } from 'motion/react';
import { Shield, Weight, Compass, Zap, Layers, Settings, Package, Anchor, CheckCircle, Crosshair, Target, ArrowRight } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

const IconMap: Record<string, any> = {
  Shield, Weight, Compass, Zap, Layers, Settings, Package, Anchor
};

export default function TechnicalDifferentiators() {
  const { content } = useBrand();

  let differentiators = [
    {
      title: "V-Hull Nose Geometry",
      description: "Geometry that actually moves water. Cuts the surface instead of pushing it. Acts as a keel. Sets your line and holds it.",
      icon: "Compass"
    },
    {
      title: "Dual Chamber Spine",
      description: "Independent air chambers. The inner spine eliminate taco-flex under full expedition load. Paddles like a hard board.",
      icon: "Shield"
    },
    {
      title: "Woven Drop-Stitch Cores",
      description: "High-density 2600g/m² knitted structural drop-stitch core provides maximum rigidity and linear stability under high PSI inflation.",
      icon: "Layers"
    },
    {
      title: "Fusion Lamination & Heat-Welded Rails",
      description: "Mechanically and thermally fuses multiple layers of industrial-grade PVC directly to the core substrate, eliminating heavy, error-prone manual glue layers.",
      icon: "Anchor"
    },
    {
      title: "Navigation White",
      description: "Reflects heat to maintain internal pressure. Maximum visibility in low light and after dark. A safety decision, not aesthetic.",
      icon: "Zap"
    },
    // {
    //   title: "32 Inch Flat Deck",
    //   description: "Wide enough to brace properly in side waves. Full rail-to-rail width. Your feet are never forced to the center.",
    //   icon: "Layers"
    // },
    // {
    //   title: "Heat-Fused PVC",
    //   description: "INDUSTRIAL GRADE CONSTRUCTION. LAYERS ARE HEAT-FUSED (NO GLUE) FOR A PERMANENT BOND. 2600G/M² KNITTED DROP-STITCH CORE.",
    //   icon: "Anchor"
    // }
  ];

  return (
    <section className="py-32 bg-[#EDEDEA] text-[#141414] relative overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 opacity-[0.4] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(rgba(20, 184, 166, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 184, 166, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}
      />

      {/* Subtle Luminous Highlights */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-ows-teal/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 border-b border-[#141414]/10 pb-12 gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-ows-teal">
              <Crosshair className="h-4 w-4" />
              <span className="text-[10px] font-mono font-bold uppercase tracking-[0.5em]">STRATEGIC ADVANTAGES</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-display font-light uppercase tracking-tighter leading-none">
              WHY THE <span className="text-ows-teal">{content.vesselName}</span>?
            </h2>
          </div>
          <div className="max-w-xs text-right">
            <p className="text-[11px] font-mono font-bold uppercase tracking-tighter text-[#141414]/60 leading-relaxed italic font-serif">
              "Engineered as a purpose-built maritime asset for the uncompromising demands of high-risk open water expeditions."
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 bg-[#141414]/5 rounded-[3rem] overflow-hidden p-1">
          {differentiators.map((item, index) => {
            const IconComp = IconMap[item.icon] || CheckCircle;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-[#141414]/5 p-12 hover:bg-ows-teal hover:text-white transition-all duration-500 group flex flex-col relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 opacity-5 font-mono text-[60px] font-bold select-none group-hover:text-white transition-colors">0{index + 1}</div>

                <div className="h-12 w-12 bg-[#141414]/5 border border-[#141414]/10 rounded-xl flex items-center justify-center mb-10 group-hover:bg-white/20 transition-all">
                  <IconComp className="h-5 w-5 text-ows-teal group-hover:text-white transition-colors" />
                </div>

                <h3 className="text-sm font-bold tracking-[0.3em] uppercase mb-6 relative z-10 text-[#141414] group-hover:text-white">
                  {item.title}
                </h3>

                <p className="text-[11px] font-mono uppercase tracking-tighter leading-relaxed text-[#141414]/50 group-hover:text-white relative z-10">
                  {item.description}
                </p>

                <div className="mt-auto pt-8 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Target className="h-3 w-3" />
                  <span className="text-[8px] font-mono font-bold uppercase tracking-widest leading-none">Verified Asset Characteristic</span>
                </div>
              </motion.div>
            )
          })}
        </div>

         <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 flex flex-col md:flex-row items-center justify-between gap-6 px-12 py-10 bg-gradient-to-r from-ows-teal to-ows-teal/80 rounded-[3rem] shadow-[0_20px_50px_rgba(20,184,166,0.3)]"
        >
           <div className="flex items-center gap-6">
              <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/10">
                 <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                 <h4 className="text-white font-display font-medium text-2xl uppercase tracking-widest leading-tight">BATCH 01 NOW OPEN</h4>
                 <p className="text-white/90 text-[11px] font-mono font-bold uppercase tracking-widest mt-1">200 UNITS. FIRST-COME, FIRST-BUILT.</p>
              </div>
           </div>
           <button 
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-10 py-5 bg-white text-[#141414] text-[10px] font-mono font-bold tracking-[0.3em] uppercase hover:bg-[#141414] hover:text-white transition-all rounded-full shadow-2xl flex items-center gap-3 shrink-0"
            >
              LOCK YOUR BATCH 01 UNIT <ArrowRight className="h-4 w-4" />
            </button>
        </motion.div>
      </div>
    </section>
  );
}
